import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../../services/api";
import Constants from "../../../services/constants";
import {
  hideLoader,
  showLoader,
  showSkelton,
  hideSkelton,
  showLoaderInside,
  hideLoaderInside,
} from "../loaderSlice";
import { showToast } from "../toastSlice";

const initialState = {
  isAvailable: false,
  projectURL: null,
  list: [],
  details: {},
  deployHistory: [],
  logs: ["logs..."],
  status: "",
  allCommits: [],
  analytics: {},
  deploymentCount: 0,
};

export const checkProjectAvailabilityAction = createAsyncThunk(
  "project/check-availability",
  async ({ name, next = () => null }, { dispatch }) => {
    try {
      dispatch(setAvailability(null));
      name = name?.replace(/[^a-zA-Z0-9-]/g, "-");
      const res = await api(
        "get",
        Constants.END_POINT.project_availability + name,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setAvailability(res?.data?.availability));
        next();
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      //   dispatch(hideLoader());
    }
  }
);

export const deployProjectAction = createAsyncThunk(
  "project/deploy",
  async ({ data, projectId, navigate, next }, { dispatch }) => {
    try {
      dispatch(showLoader());
      data = { ...data, name: data?.name?.replace(/[^a-zA-Z0-9-]/g, "-") };
      const res = await api(
        "post",
        projectId
          ? Constants.END_POINT.project_update + projectId
          : Constants.END_POINT.project_deploy,
        data
      );
      if (res?.success && res?.data) {
        let deployment_id = projectId
          ? res?.data?.deployment_uuid
          : res?.data?.deployment_id;
        let project = projectId ? projectId : res?.data?.project_id;
        // Success: project deployed or updated - important notification
        dispatch(
          showToast({
            severity: "success",
            summary: projectId
              ? "Project update initiated"
              : "Project deployment initiated",
            persistent: true, // Important operation - add to notifications panel
          })
        );

        if (projectId) next();
        else
          navigate(
            `/project/${data?.name}/build-logs?deployment-id=${deployment_id}`,{state:{id:res.data.project_id}}
          );
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: res?.data?.message,
            persistent: true, // Important error - add to notifications panel
          })
        );
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error.message,
          persistent: true, // Important error - add to notifications panel
        })
      );
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const changeNameAction = createAsyncThunk(
  "project/change-name",
  async ({ data, projectId, next }, { dispatch }) => {
    try {
      dispatch(showLoader());
      data = { name: data?.name?.replace(/[^a-zA-Z0-9-]/g, "-") };
      const res = await api(
        "post",
        Constants.END_POINT.project_name_update + projectId,
        data
      );

      if (res?.success && res?.data) {
        dispatch(showToast({ severity: "success", summary: res.message }));
        next();
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const updateProjectStatusAction = createAsyncThunk(
  "project/change-name",
  async ({ data, projectId, next }, { dispatch }) => {
    try {
      dispatch(showLoader());

      const res = await api(
        "post",
        Constants.END_POINT.project + "toggle/" + projectId,
        data
      );

      if (res?.success && res?.data) {
        // dispatch(showToast({ severity: "success", summary: res.message }));
        next();
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deployLatestCommitAction = createAsyncThunk(
  "project/deploy_latest",
  async ({ data, navigate }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "post",
        Constants.END_POINT.project_deploy_latest,
        data
      );

      if (res?.success && res?.data) {

        dispatch(
          showToast({
            severity: "success",
            summary: "Latest commit deployment initiated",
            persistent: true, // Important operation - add to notifications panel
          })
        );
        navigate(
          `/project/${data?.name}/build-logs?deployment-id=${res?.data?.deployment_id}`,{state:{id:res?.data?.project_id}}
        );
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: res.message,
            persistent: true, // Important error - add to notifications panel
          })
        );
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error.message,
          persistent: true, // Important error - add to notifications panel
        })
      );
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deploySpecificCommitAction = createAsyncThunk(
  "project/deploy_latest",
  async ({ data, navigate }, { dispatch }) => {
    try {
      dispatch(showLoader());

      const res = await api(
        "post",
        Constants.END_POINT.project_deploy_specific_commit,
        data
      );

      if (res?.success && res?.data) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Specific commit deployment initiated",
            persistent: true, // Important operation - add to notifications panel
          })
        );
        navigate(
          `/project/${data?.name}/build-logs?deployment-id=${res?.data?.deployment_id}`,{state:{id:res?.data?.project_id}}
        );
      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: res.message,
            persistent: true, // Important error - add to notifications panel
          })
        );
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(
        showToast({
          severity: "error",
          summary: error.message,
          persistent: true, // Important error - add to notifications panel
        })
      );
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const deployRollBackAction = createAsyncThunk(
  "project/deploy_rollback",
  async ({ data, next }, { dispatch }) => {
    try {
      dispatch(showLoader());

      const res = await api(
        "post",
        Constants.END_POINT.project + "rollback",
        data
      );

      if (res?.success) {
        dispatch(showToast({ severity: "success", summary: res.message }));
        dispatch(projectDeployHistoryAction({ projectId: data?.project_id }));
        next();
        // navigate(
        //   "/projects/build-logs?deployment-id=" +
        //     res?.data?.deployment_id +
        //     "&project-id=" +
        //     res?.data?.project_id
        // );
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const getProjectsAction = createAsyncThunk(
  "project/get_list",
  async ({ data, next }, { dispatch }) => {
    dispatch(showSkelton());
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project_list + `?id=${data}`,
        {}
      );
      if (res?.success && res?.data) {
        let projectsWithDetails = res?.data?.map((project) => ({
          ...project,
          status: project?.Deployments[0]?.status || "N/A",
          branch: project?.Deployments[0]?.branch || "N/A",
          commit_id: project?.Deployments[0]?.commit_id || "N/A",
          enable: project?.Deployments[0]?.enable || false,
          lastDeployedAt: project?.Deployments[0]?.createdAt || null,
          created_by_user: project?.created_by_user,
        }));

        dispatch(setProjects(projectsWithDetails));
        dispatch(setActiveProject(projectsWithDetails[0]));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      dispatch(hideSkelton());
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideSkelton());
    }
  }
);

export const getProjectDetailsAction = createAsyncThunk(
  "project/get_details",
  async ({ projectId, activeOrg, navigate }, { dispatch }) => {
    try {
      const res = await api(
        "get",
        `${Constants.END_POINT.project_single}${projectId}`,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setProjectDetails(res?.data));
        if (res?.data?.organization_id !== activeOrg?.id) {
          navigate("/projects");
        }
      } else {
        return Promise.reject(res?.message);
      }
    } catch (error) {
      return Promise.reject("An error occurred.");
    } finally {
    }
  }
);

export const getProjectStatusAction = createAsyncThunk(
  "project/get_status",
  async ({ deployment_uuid }, { dispatch }) => {
    // dispatch(showSkelton());
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project_deployment_status + deployment_uuid,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setProjectStatus(res?.data));
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res?.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      // dispatch(hideSkelton());
    }
  }
);

export const projectDeployHistoryAction = createAsyncThunk(
  "project/check-availability",
  async ({ projectId, limit = 10, page = 1 }, { dispatch }) => {
    dispatch(showSkelton());
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project +
          projectId +
          `/get-deployments?limit=${limit}&page=${page}`,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setDeployHistory(res?.data));
        dispatch(setDeploymentCount(res?.count));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideSkelton());
    }
  }
);

export const getAllCommitsAction = createAsyncThunk(
  "project/get-commits",
  async ({ projectId }, { dispatch }) => {
    dispatch(showLoaderInside());
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project + projectId + "/commits",
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setAllCommits(res?.data));
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoaderInside());
    }
  }
);

export const getProjectURLAction = createAsyncThunk(
  "project/get-commits",
  async ({ projectId }, { dispatch }) => {
    // dispatch(showLoaderInside())
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project + "live-url/" + projectId,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setProjectURL(res?.data));
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      // dispatch(hideLoaderInside());
    }
  }
);

export const getProjectAnalyticsAction = createAsyncThunk(
  "project/get-commits",
  async ({ projectId }, { dispatch }) => {
    // dispatch(showLoaderInside())
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project_analytics + projectId,
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setProjectsAnalytics(res?.data));
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      // dispatch(hideLoaderInside());
    }
  }
);

export const getProjectLogAction = createAsyncThunk(
  "project/get_log",
  async ({ deployment_uuid }, { dispatch }) => {
    dispatch(setProjectLogs([]));
    try {
      const res = await api(
        "get",
        Constants.END_POINT.project_deployment + deployment_uuid + "/logs",
        {}
      );
      if (res?.success && res?.data) {
        dispatch(setProjectLogs(res?.data));
      } else {
        // dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      //   dispatch(hideLoader());
    }
  }
);

export const deleteProjectAction = createAsyncThunk(
  "project/get_log",
  async ({ projectId, navigate }, { dispatch }) => {
    dispatch(setProjectLogs([]));
    dispatch(showLoader());

    try {
      const res = await api(
        "delete",
        Constants.END_POINT.project + projectId,
        {}
      );
      if (res?.success) {
        return res;

      } else {
        dispatch(
          showToast({
            severity: "error",
            summary: res?.data?.message || res?.message,
          })
        );
        return Promise.reject(res?.data?.message || res?.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error?.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const selectProject = createAsyncThunk(
  "project/get_log",
  async ({ projectId, navigate }, { dispatch }) => {
    try {
    } catch (error) {
      // dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);
export const purgeProjectCacheAction = createAsyncThunk(
  "project/purge-cache",
  async ({ projectId, next = () => null }, { dispatch }) => {
    try {
      dispatch(showLoader());
      const res = await api(
        "delete",
        Constants.END_POINT.project + "purge-cache/" + projectId,
        {}
      );
      if (res?.success) {
        dispatch(showToast({ severity: "success", summary: res.message }));
        next();
      } else {
        dispatch(showToast({ severity: "error", summary: res.message }));
        return Promise.reject(res.message);
      }
    } catch (error) {
      dispatch(showToast({ severity: "error", summary: error.message }));
      return Promise.reject("An error occurred.");
    } finally {
      dispatch(hideLoader());
    }
  }
);

const projectSlice = createSlice({
  name: "project",
  initialState,
  reducers: {
    setAvailability: (state, action) => {
      state.isAvailable = action.payload;
    },
    setProjectURL: (state, action) => {
      state.projectURL = action.payload;
    },
    setProjects: (state, action) => {
      state.list = action.payload;
    },
    setProjectsAnalytics: (state, action) => {
      state.analytics = action.payload;
    },
    setProjectDetails: (state, action) => {
      state.details = action.payload;
    },
    setDeployHistory: (state, action) => {
      state.deployHistory = action.payload;
    },
    setProjectStatus: (state, action) => {
      state.status = action.payload;
    },
    setProjectLogs: (state, action) => {
      state.logs = action.payload;
    },
    setAllCommits: (state, action) => {
      state.allCommits = action.payload;
    },
    setActiveProject: (state, action) => {
      state.activeProject = action.payload;
    },
    setDeploymentCount: (state, action) => {
      state.deploymentCount = action.payload;
    },
  },
});

export const {
  setAvailability,
  setDeployHistory,
  setProjects,
  setProjectLogs,
  setProjectDetails,
  setProjectStatus,
  setAllCommits,
  setProjectURL,
  setProjectsAnalytics,
  setActiveProject,
  setDeploymentCount,
} = projectSlice.actions;

export default projectSlice.reducer;
